import React, { useState, useRef, useCallback } from 'react';
import { fuzzyMatchAccount } from '../../utils/balanceUtils';

interface AccountAutocompleteProps {
  value: string;
  accounts: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
}

export const AccountAutocomplete: React.FC<AccountAutocompleteProps> = ({
  value,
  accounts,
  onChange,
  placeholder = 'Conta',
  className = '',
  required,
}) => {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const listRef = useRef<HTMLUListElement>(null);

  const getMatches = useCallback(() => {
    if (!value.trim()) return accounts.slice(0, 10);
    return accounts
      .map((a) => ({ account: a, score: fuzzyMatchAccount(a, value) }))
      .filter((m) => m.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
      .map((m) => m.account);
  }, [value, accounts]);

  const matches = open ? getMatches() : [];

  const select = (account: string) => {
    onChange(account);
    setOpen(false);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || matches.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev <= 0 ? matches.length - 1 : prev - 1));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      select(matches[highlightIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlightIndex(-1);
    }
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlightIndex(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className || 'w-full border rounded-lg px-3 py-2 text-sm'}
        required={required}
      />
      {matches.length > 0 && (
        <ul
          ref={listRef}
          className="absolute z-10 bg-white border rounded-lg shadow mt-1 max-h-40 overflow-y-auto w-full"
        >
          {matches.map((acc, idx) => (
            <li
              key={acc}
              className={`px-3 py-1.5 text-sm cursor-pointer ${
                idx === highlightIndex ? 'bg-blue-100' : 'hover:bg-blue-50'
              }`}
              onMouseDown={() => select(acc)}
              onMouseEnter={() => setHighlightIndex(idx)}
            >
              {acc}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
